import React, { useState, useEffect, useRef } from 'react';
import { Smartphone, Loader2, X, RefreshCw, AlertTriangle } from 'lucide-react';
import ProductExtractionResults from './ProductExtractionResults';

const API_URL = import.meta.env.VITE_API_URL || '';

interface ExtractedProduct {
  name: string;
  brand?: string | null;
  category?: string | null;
  confidence: number;
  quantity?: number | null;
}

interface UploadedImage {
  s3_key: string;
  url: string;
  extracted_products: ExtractedProduct[];
  extraction_status: 'pending' | 'processing' | 'completed' | 'failed';
  image_quality?: string | null;
  upload_timestamp: string;
  localPreview?: string;
}

interface DesktopQRUploadProps {
  surveyId: number;
  sessionId: number;
  questionId: string;
  questionText?: string;
  maxImages?: number;
  onImagesChange: (images: UploadedImage[]) => void;
  images: UploadedImage[];
  disabled?: boolean;
  onTextFallback?: (text: string) => void;
  textFallback?: string;
}

const MAX_POLL_COUNT = 900; // 900 * 2 seconds = 30 minutes

const DesktopQRUpload: React.FC<DesktopQRUploadProps> = ({
  surveyId,
  sessionId,
  questionId,
  questionText = "",
  maxImages = 3,
  onImagesChange,
  images,
  disabled = false,
  onTextFallback,
  textFallback = ''
}) => {
  const [qrCode, setQrCode] = useState<string | null>(null);
  const [uploadToken, setUploadToken] = useState<string | null>(null);
  const [isPolling, setIsPolling] = useState(false);
  const [isCreatingSession, setIsCreatingSession] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [sessionExpiringSoon, setSessionExpiringSoon] = useState(false);
  const [showTextFallback, setShowTextFallback] = useState(false);
  const [fallbackReason, setFallbackReason] = useState<string>('');
  const [textInput, setTextInput] = useState(textFallback);
  const pollIntervalRef = useRef<NodeJS.Timeout | null>(null);
  const pollCountRef = useRef(0);

  // Refs that mirror the latest props so the long-lived setInterval callback
  // (registered once on mount) always reads current values instead of the
  // closure from the first render. Without these, the poll callback would
  // keep calling the very first `onImagesChange` it saw — overwriting any
  // text_fallback the respondent typed afterwards.
  const imagesRef = useRef(images);
  const onImagesChangeRef = useRef(onImagesChange);
  const maxImagesRef = useRef(maxImages);

  useEffect(() => {
    imagesRef.current = images;
  }, [images]);

  useEffect(() => {
    onImagesChangeRef.current = onImagesChange;
  }, [onImagesChange]);

  useEffect(() => {
    maxImagesRef.current = maxImages;
  }, [maxImages]);

  useEffect(() => {
    setTextInput(textFallback);
  }, [textFallback]);

  useEffect(() => {
    createUploadSession();
    return () => {
      if (pollIntervalRef.current) {
        clearInterval(pollIntervalRef.current);
      }
    };
  }, []);

  // Auto-clear error after 5 seconds
  useEffect(() => {
    if (error) {
      const timer = setTimeout(() => setError(null), 5000);
      return () => clearTimeout(timer);
    }
  }, [error]);

  // Stop polling when max images reached
  useEffect(() => {
    if (images.length >= maxImages && pollIntervalRef.current) {
      clearInterval(pollIntervalRef.current);
      pollIntervalRef.current = null;
      setIsPolling(false);
    }
  }, [images.length, maxImages]);

  const createUploadSession = async () => {
    setIsCreatingSession(true);
    setError(null);
    setSessionExpiringSoon(false);
    pollCountRef.current = 0;

    try {
      const response = await fetch(`${API_URL}/api/photo-upload/session`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          survey_id: surveyId,
          session_id: sessionId,
          question_id: questionId,
          question_text: questionText
        }),
        credentials: 'include'
      });

      if (!response.ok) {
        throw new Error('Failed to create upload session');
      }

      const data = await response.json();
      setQrCode(data.qr_code_data);
      setUploadToken(data.upload_token);
      startPolling(data.upload_token);
    } catch (err) {
      setError('Failed to generate QR code. Please try again.');
    } finally {
      setIsCreatingSession(false);
    }
  };

  const startPolling = (token: string) => {
    setIsPolling(true);
    pollCountRef.current = 0;

    pollIntervalRef.current = setInterval(async () => {
      // Always read the latest props through refs so we never act on stale
      // closure values. See the comment near the ref declarations above.
      const currentImages = imagesRef.current;
      const currentMaxImages = maxImagesRef.current;
      const emitImages = onImagesChangeRef.current;

      // Stop polling if max images reached
      if (currentImages.length >= currentMaxImages) {
        if (pollIntervalRef.current) {
          clearInterval(pollIntervalRef.current);
          pollIntervalRef.current = null;
        }
        setIsPolling(false);
        return;
      }

      // Check if exceeded max poll count
      pollCountRef.current++;
      if (pollCountRef.current >= MAX_POLL_COUNT) {
        if (pollIntervalRef.current) {
          clearInterval(pollIntervalRef.current);
          pollIntervalRef.current = null;
        }
        setIsPolling(false);
        setSessionExpiringSoon(true);
        return;
      }

      try {
        const response = await fetch(
          `${API_URL}/api/photo-upload/session/${token}/status`,
          { credentials: 'include' }
        );

        if (!response.ok) {
          // Server error - don't stop polling, just skip this iteration
          console.error('Polling response not ok:', response.status);
          return;
        }

        const data = await response.json();

        if (data.expired) {
          if (pollIntervalRef.current) {
            clearInterval(pollIntervalRef.current);
            pollIntervalRef.current = null;
          }
          setIsPolling(false);
          setQrCode(null);
          setUploadToken(null);
          setSessionExpiringSoon(true);
          return;
        }

        if (data.images && data.images.length > currentImages.length) {
          emitImages(data.images);
        } else if (data.images && data.images.length > 0) {
          // Same count but extraction status may have updated (Pass 2 finished)
          const sameKeys = data.images.length === currentImages.length &&
            data.images.every((img: UploadedImage, i: number) => img.s3_key === currentImages[i]?.s3_key);
          const statusChanged = sameKeys && data.images.some(
            (img: UploadedImage, i: number) =>
              img.extraction_status !== currentImages[i]?.extraction_status ||
              (img.extracted_products?.length || 0) !== (currentImages[i]?.extracted_products?.length || 0)
          );
          if (statusChanged) {
            emitImages(data.images);
          }
        }
        // Check if any image needs text fallback (Pass 1 OR Pass 2 failure)
        if (data.images && data.images.length > 0) {
          const fallbackImage = data.images.find(
            (img: UploadedImage & { show_text_fallback?: boolean }) => img.show_text_fallback
          ) as (UploadedImage & { show_text_fallback?: boolean; rejection_reason?: string }) | undefined;
          if (fallbackImage) {
            setShowTextFallback(true);
            if (fallbackImage.rejection_reason) {
              setFallbackReason(fallbackImage.rejection_reason);
            }
          }
        }
      } catch (err) {
        console.error('Polling error:', err);
      }
    }, 2000);
  };

  const handleRegenerateQR = () => {
    if (pollIntervalRef.current) {
      clearInterval(pollIntervalRef.current);
      pollIntervalRef.current = null;
    }
    createUploadSession();
  };

  const handleRemoveImage = (s3Key: string) => {
    const updatedImages = images.filter(img => img.s3_key !== s3Key);
    onImagesChange(updatedImages);
  };

  const canAddMore = images.length < maxImages;

  return (
    <div className="space-y-6">
      {images.length > 0 && (
        <>
          <div className="flex items-center gap-2 text-sm text-emerald-600 bg-emerald-50 rounded-lg p-3">
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
            </svg>
            <span>Photos received from your phone!</span>
          </div>
          <div className="grid grid-cols-2 gap-4">
          {images.map((image) => (
            <div key={image.s3_key} className="relative bg-white rounded-lg border border-gray-200 overflow-hidden">
              <div className="aspect-video relative bg-gray-100">
                <img
                  src={image.localPreview || `${API_URL}${image.url}`}
                  alt="Uploaded photo"
                  className="w-full h-full object-cover"
                />
                <button
                  onClick={() => handleRemoveImage(image.s3_key)}
                  className="absolute top-2 right-2 p-1.5 bg-red-500 text-white rounded-full
                             hover:bg-red-600 transition-colors shadow-md"
                >
                  <X className="w-4 h-4" />
                </button>

                {image.extraction_status === 'processing' && (
                  <div className="absolute inset-0 bg-black/30 flex items-center justify-center">
                    <div className="bg-white rounded-lg px-3 py-1.5 flex items-center gap-2">
                      <Loader2 className="w-4 h-4 animate-spin text-[#07E5D1]" />
                      <span className="text-sm">Analyzing...</span>
                    </div>
                  </div>
                )}
              </div>

              {image.extraction_status === 'completed' && image.extracted_products?.length > 0 && (
                <ProductExtractionResults
                  products={image.extracted_products}
                  showImage={false}
                />
              )}

              {image.extraction_status === 'failed' && (
                <div className="p-2 bg-amber-50 text-amber-700 text-sm">
                  No products detected
                </div>
              )}
            </div>
          ))}
          </div>
        </>
      )}

      {canAddMore && (
        <div className="bg-gray-50 rounded-xl p-8 text-center border border-gray-200">
          <Smartphone className="w-12 h-12 text-[#07E5D1] mx-auto mb-4" />
          <h3 className="text-lg font-semibold text-gray-800 mb-2">
            Scan to Take Photos
          </h3>
          <p className="text-gray-600 text-sm mb-6">
            Use your phone to capture photos of your pantry or fridge
          </p>

          {isCreatingSession ? (
            <div className="flex items-center justify-center gap-2 text-gray-500">
              <Loader2 className="w-5 h-5 animate-spin" />
              <span>Generating QR code...</span>
            </div>
          ) : sessionExpiringSoon ? (
            <div className="space-y-4">
              <div className="flex items-center justify-center gap-2 text-amber-600">
                <AlertTriangle className="w-5 h-5" />
                <span className="text-sm font-medium">QR Code Expired</span>
              </div>
              <p className="text-xs text-gray-500">
                The QR code has expired. Generate a new one to continue.
              </p>
              <button
                onClick={handleRegenerateQR}
                className="px-4 py-2 bg-[#07E5D1] text-white rounded-lg font-medium
                           hover:bg-[#06D4C2] transition-colors inline-flex items-center gap-2"
              >
                <RefreshCw className="w-4 h-4" />
                Generate New QR Code
              </button>
            </div>
          ) : qrCode ? (
            <div className="space-y-4">
              <div className="inline-block p-4 bg-white rounded-lg shadow-sm border border-gray-100">
                <img src={qrCode} alt="Scan QR code" className="w-48 h-48" />
              </div>

              {/* Step indicator */}
              <div className="text-xs text-gray-500 space-y-1">
                <p>1. Scan QR code with your phone camera</p>
                <p>2. Take photos of your pantry/fridge</p>
                <p>3. Tap "Done" on your phone when finished</p>
              </div>

              <div className="flex items-center justify-center gap-2 text-sm text-[#07E5D1] bg-[#E8FCFA] rounded-lg py-2 px-4">
                <Loader2 className="w-4 h-4 animate-spin" />
                <span>Waiting for photos from your phone...</span>
              </div>

              <button
                onClick={handleRegenerateQR}
                className="text-xs text-gray-400 hover:text-gray-600 flex items-center gap-1 mx-auto"
              >
                <RefreshCw className="w-3 h-3" />
                QR not working? Generate new code
              </button>
            </div>
          ) : (
            <div className="space-y-4">
              <p className="text-sm text-gray-500">QR code unavailable</p>
              <button
                onClick={handleRegenerateQR}
                className="text-sm text-[#07E5D1] hover:text-[#06D4C2] flex items-center gap-1 mx-auto"
              >
                <RefreshCw className="w-4 h-4" />
                Try again
              </button>
            </div>
          )}
        </div>
      )}

      {error && (
        <p className="text-sm text-red-600 text-center">{error}</p>
      )}

      {/* Text fallback when image validation/extraction fails */}
      {showTextFallback && onTextFallback && (
        <div className="bg-amber-50 border border-amber-200 rounded-lg p-4 space-y-3">
          <div className="flex items-start gap-2">
            <AlertTriangle className="w-5 h-5 text-amber-600 flex-shrink-0 mt-0.5" />
            <div>
              <p className="text-sm font-medium text-amber-800">Photo not recognized</p>
              <p className="text-xs text-amber-600 mt-1">
                {fallbackReason || "We couldn't identify products in your photo. You can type your answer instead."}
              </p>
            </div>
          </div>
          <div className="space-y-2">
            <textarea
              value={textInput}
              onChange={(e) => {
                const next = e.target.value;
                setTextInput(next);
                onTextFallback(next);
              }}
              placeholder="Type your answer here..."
              rows={3}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-[#07E5D1]"
            />
            {textInput.trim().length > 0 && (
              <p className="text-xs text-emerald-700">
                Saved — you can continue to the next question.
              </p>
            )}
          </div>
        </div>
      )}

      <p className="text-xs text-gray-500 text-center">
        {images.length} of {maxImages} photos
      </p>
    </div>
  );
};

export default DesktopQRUpload;
